import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  Grid,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  Typography,
  useTheme,
  Badge,
  Fab,
  Collapse,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import FilterListIcon from "@mui/icons-material/FilterList";
import StyledTableRow from "../../../Component/Common/StyledTableRow";
import StyledTableCell from "../../../Component/Common/StyledTableCell";
import StatusButton from "../../../Component/Common/StatusButton";
import IconBackground from "../../../Component/Common/IconBackground";
import CommonPagination from "../../../Component/Common/CommonPagination";
import NoDataFound from "../../../Component/Common/NoDataFound";
import CommonModal from "../../../Component/Common/CommonModal";
import CommonTextField from "../../../Component/Common/CommonTextField";
import ErrorMessage from "../../../Component/Common/ErrorMessage";
import { useCommonStyles } from "../../../utils";
import { createProduct, getProducts, deleteProduct, getProductById } from "../../../Redux/Actions/Inventory";
import { useDispatch } from "react-redux";
import useSnackBar from "../../../Redux/hooks/useSnackBar";
import usePageLoader from "../../../Redux/hooks/usePageLoader";
import { useConfirm } from "material-ui-confirm";
import CommonInventoryFilterDrawer from "../CommonInventoryFilterDrawer";
import KeyboardArrowUpIcon from "@mui/icons-material/KeyboardArrowUp";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";

const Product = () => {
  const theme = useTheme();
  const { classes } = useCommonStyles();
  const dispatch = useDispatch();
  const { setSnackBar } = useSnackBar();
  const setFullPageLoader = usePageLoader();
  const confirmDialogue = useConfirm();

  const PerPage = 5;

  // ===== States =====
  const [page, setPage] = useState(1);
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [productName, setProductName] = useState("");
  const [errors, setErrors] = useState({});
  const [selectedItem, setSelectedItem] = useState(null);
  const [listData, setListData] = useState([]);
  const [isOpenFilterDrawer, setIsOpenFilterDrawer] = useState(false);
  const initialFilterState = { status: "" };
  const [filterData, setFilterData] = useState(initialFilterState);
  const [isApllyFilter, setIsApplyFilter] = useState(1);
  const [totalCount, setTotalCount] = useState(0);
  const [openRow, setOpenRow] = useState(null);
  const [SubData, setSubData] = useState([]);
  const [subPage, setSubPage] = useState(1);
  const filterDataCount = filterData?.status?.trim() ? 1 : 0;
  const tabelHeader = ["SR.", "Product", "Created date", "Quantity", "Action"];
  // ===== Pagination =====
  const handleChangePage = (e, p) => setPage(p);



  const SubPerPage = 5;

  const handleChangeSubPage = (event, newPage) => {
    setSubPage(newPage);
  };

  // Get paginated data
  const paginatedSubData = SubData.slice(
    (subPage - 1) * SubPerPage,
    subPage * SubPerPage
  );



  const _getProductById = async (index) => {
    setFullPageLoader(true);
    try {
      const dataa = paginatedData[index]
      const body = { id: dataa?._id };
      const data = await dispatch(getProductById(body));
      setSubData(data?.payload?.data?.data?.purchaseOrders || [])
    } catch (error) {
      console.error("Error fetching products:", error);
      setSnackBar("error", "Failed to fetch products");
    }
    setFullPageLoader(false);
  }

  // ===== Validation =====
  const validStep = () => {
    let error = {};
    let formIsValid = true;
    if (!productName?.trim()) {
      formIsValid = false;
      error["productName"] = "* Please enter product name.";
    }
    setErrors(error);
    return formIsValid;
  };

  // ===== Fetch Products =====
  const fetchProductData = async () => {
    setFullPageLoader(true);
    try {
      let body = {
        limit: PerPage,
        page,
        status: isApllyFilter && filterData?.status?.trim() ? +filterData.status : null,
      };
      const data = await dispatch(getProducts(body));
      setListData(data?.payload?.data?.data || []);
      setTotalCount(data?.payload?.data?.pagination?.total || 0);
    } catch (error) {
      console.error("Error fetching products:", error);
      setSnackBar("error", "Failed to fetch products");
    }
    setFullPageLoader(false);
  };

  useEffect(() => {
    fetchProductData();
  }, [page]);

  useEffect(() => {
    setPage(1);
    fetchProductData();
  }, [isApllyFilter]);

  // ===== Add / Update Product =====
  const handleAddOrUpdate = async () => {
    if (!validStep()) return;

    setFullPageLoader(true);
    try {
      const body = { name: productName };
      if (selectedItem?._id) body.id = selectedItem._id;

      const res = await dispatch(createProduct(body));
      if (res?.payload?.data?.code == 1) {
        setSnackBar("success", res?.payload?.message || `Product ${selectedItem?._id ? "updated" : "added"} successfully`);
        fetchProductData();
        setIsOpenModal(false);
        setSelectedItem(null);
        setProductName("");
        setErrors({});
      } else {
        setSnackBar("error", res?.payload?.message || "Something went wrong");
      }
    } catch (error) {
      console.error("Error adding/updating product:", error);
      setSnackBar("error", "Something went wrong");
    }
    setFullPageLoader(false);
  };

  // ===== Edit Product =====
  const handleEdit = (item) => {
    setSelectedItem(item);
    setProductName(item.name);
    setIsOpenModal(true);
  };

  // ===== Delete Product =====
  const handleDelete = async (id) => {
    confirmDialogue({ title: "Are you sure you want to delete this product?" })
      .then(async () => {
        setFullPageLoader(true);
        try {
          const res = await dispatch(deleteProduct(`/${id}`));
          if (res?.payload?.data?.code == 1) {
            setSnackBar("success", res?.payload?.message || "Product deleted successfully");
            fetchProductData();
          } else {
            setSnackBar("error", res?.payload?.message || "Failed to delete product");
          }
        } catch (error) {
          console.error("Error deleting product:", error);
          setSnackBar("error", "Something went wrong while deleting");
        }
        setFullPageLoader(false);
      })
      .catch(() => { });
  };

  const paginatedData = listData.slice((page - 1) * PerPage, page * PerPage);

  return (
    <Grid container spacing={2} padding={"25px"}>
      <Grid item xs={12} display={"flex"} justifyContent={"space-between"} alignItems={"center"} flexWrap={"wrap"}>
        <Typography fontFamily={"Satoshi-Bold"} fontSize={"28px"}>Product List</Typography>
        <Box display={"flex"} gap={1} flexWrap={"wrap"}>
          <Badge
            badgeContent={filterDataCount || 0}
            sx={{ "& .MuiBadge-badge": { right: "4px", top: "10px", zIndex: 999, color: "#FFF" } }}
            color="primary"
          >
            <Button
              startIcon={<FilterListIcon />}
              className={classes.cancelButton}
              onClick={() => setIsOpenFilterDrawer(true)}
            >
              Filters
            </Button>
          </Badge>
          <Button
            startIcon={<AddIcon />}
            onClick={() => {
              setSelectedItem(null);
              setProductName("");
              setErrors({});
              setIsOpenModal(true);
            }}
            className={classes.saveButton}
          >
            Add Product
          </Button>
        </Box>
      </Grid>

      <Grid item xs={12}>
        <Box sx={{ borderRadius: "26px", border: "1px solid", backgroundColor: theme.palette.bgWhite?.main || "#fff", borderColor: "#c8d3d96e", borderTop: "none" }}>
          <TableContainer>
            <Table sx={{ borderBottom: "1px solid #c8d3d96e" }}>
              <TableHead>
                <StyledTableRow isHeader style={{ backgroundColor: "#E9E9E9" }}>
                  {tabelHeader?.map((ele, idx) => <StyledTableCell key={idx}>{ele}</StyledTableCell>)}
                </StyledTableRow>
              </TableHead>
              <TableBody>
                {paginatedData.length > 0 ? paginatedData.map((row, index) => (
                  <React.Fragment key={index}>
                    <StyledTableRow key={row._id}>
                      <StyledTableCell>{index + 1 + (page - 1) * PerPage}</StyledTableCell>
                      <StyledTableCell>{row.name || "NA"}</StyledTableCell>
                      <StyledTableCell>{new Date(row.createdAt).toLocaleDateString()}</StyledTableCell>
                      {/* <StyledTableCell>
                      <StatusButton text={row.status === 2 ? "Not In Use" : "In Use"} color={row.status === 2 ? "gray" : "success"} />
                    </StyledTableCell> */}
                      <StyledTableCell>{row.productQuantity || "NA"}</StyledTableCell>
                      <StyledTableCell>
                        <Box sx={{ display: "flex", gap: 1, flexDirection: "row" }}>
                          <Box display={"flex"} gap={1}>
                            <IconBackground icon={"edit"} onClick={() => handleEdit(row)} />
                            {/* <IconBackground icon={"delete"} onClick={() => handleDelete(row._id)} /> */}
                          </Box>
                          <Box display={"flex"} gap={1}>
                            {
                              <Fab
                                sx={{
                                  height: '36px',
                                  width: '36px',
                                  minHeight: "30px",
                                  backgroundColor: theme.palette.primary.main,
                                  boxShadow: "none",
                                  "&.MuiFab-root.Mui-disabled": {
                                    backgroundColor: 'transparent'
                                  },
                                  "&:hover": { backgroundColor: theme.palette.primary.main },
                                }}
                                onClick={() => {
                                  const isOpening = index !== openRow; // true if opening this row
                                  setOpenRow(isOpening ? index : null);

                                  if (isOpening) {
                                    _getProductById(index); // fetch or set subData when opening
                                  }
                                  else {
                                    setSubData([]); // clear subData when closing
                                  }
                                }}
                              >
                                {openRow === index ? <KeyboardArrowUpIcon sx={{ color: "#FFF" }} /> : <KeyboardArrowDownIcon sx={{ color: "#FFF" }} />}
                              </Fab>}
                          </Box>

                        </Box>

                      </StyledTableCell>

                    </StyledTableRow>
                    <StyledTableRow>
                      <StyledTableCell colSpan={12} style={{ padding: "0px 10px 0px 10px" }}>
                        <Collapse in={openRow === index} timeout="auto" unmountOnExit>
                          <Box margin={1}>
                            <Typography fontSize={'18px'} fontFamily={"Satoshi-Bold"} ml={1} mb={1}>
                              All Items
                            </Typography>

                            <Box sx={{ borderRadius: "26px", border: "1px solid", backgroundColor: theme.palette.bgWhite.main, borderColor: "#c8d3d96e", borderTop: "none" }}>
                              <TableContainer>
                                <Table>
                                  <TableHead>
                                    <StyledTableRow isHeader>
                                      <StyledTableCell>SR.</StyledTableCell>
                                      <StyledTableCell>Item Name</StyledTableCell>
                                      <StyledTableCell>Quantity</StyledTableCell>
                                    </StyledTableRow>
                                  </TableHead>

                                  <TableBody>
                                    {paginatedSubData?.length > 0 ? (
                                      paginatedSubData.map((subRow, subIndex) => (
                                        <StyledTableRow key={subIndex}>
                                          <StyledTableCell>{index + 1 + (subPage - 1) * SubPerPage}</StyledTableCell>
                                          <StyledTableCell>
                                            <Box sx={{ display: "flex",}}>
                                              <Box backgroundColor="#F7FAFC" sx={{ border: "1px solid #13131333", borderRadius: "6px", padding: "11px 10px", width: "fit-content", textAlign: "center" }}>
                                                <Typography fontFamily={"Satoshi-Medium"} fontSize={'12px'} noWrap>{subRow.itemName || "NA"}</Typography>
                                              </Box>
                                            </Box>
                                          </StyledTableCell>
                                          <StyledTableCell>{subRow.quantity || "NA"}</StyledTableCell>
                                        </StyledTableRow>
                                      ))
                                    ) : (
                                      <StyledTableRow>
                                        <StyledTableCell colSpan={13}>
                                          <NoDataFound />
                                        </StyledTableCell>
                                      </StyledTableRow>
                                    )}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                              <CommonPagination
                                totalCount={SubData.length}
                                count={Math.ceil(SubData.length / SubPerPage)}
                                itemsPerPage={SubPerPage}
                                page={subPage}
                                onChange={handleChangeSubPage}
                              />
                            </Box>
                          </Box>
                        </Collapse>
                      </StyledTableCell>
                    </StyledTableRow>

                  </React.Fragment>

                )) : (
                  <StyledTableRow>
                    <StyledTableCell colSpan={12}><NoDataFound /></StyledTableCell>
                  </StyledTableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <CommonPagination
            totalCount={listData.length}
            count={Math.ceil(listData.length / PerPage)}
            itemsPerPage={PerPage}
            page={page}
            onChange={handleChangePage}
          />
        </Box>
      </Grid>

      <CommonInventoryFilterDrawer
        isOpenFilterDrawer={isOpenFilterDrawer}
        setIsOpenFilterDrawer={setIsOpenFilterDrawer}
        setFilterData={setFilterData}
        initialFilterState={initialFilterState}
        filterData={filterData}
        setIsApplyFilter={setIsApplyFilter}
        isApllyFilter={isApllyFilter}
      />

      <CommonModal
        open={isOpenModal}
        onClose={() => { setIsOpenModal(false); setSelectedItem(null); setErrors({}); setProductName(""); }}
        title={`${selectedItem?._id ? "Edit" : "Add"} Product`}
        submitButtonName={selectedItem?._id ? "Update" : "Add"}
        onHandleCancel={() => { setErrors({}); setProductName(""); setIsOpenModal(false); }}
        onHandleSubmit={handleAddOrUpdate}
      >
        <Grid container>
          <Grid item xs={12}>
            <CommonTextField
              valid
              type="text"
              label="Product Name"
              name="productName"
              value={productName}
              placeholder="Enter The Product"
              onChange={(e) => setProductName(e.target.value)}
            />
            <ErrorMessage text={errors?.productName} />
          </Grid>
        </Grid>
      </CommonModal>
    </Grid>
  );
};

export default Product;
